/**
 * Service account API service
 */
import { apiGet, apiPost, apiDelete } from './api'
import type { ApiResult } from '../types/api'
import type {
  ServiceAccountInfo,
  NewServiceAccount,
  ServiceAccountCreated,
  NewTokenRequest,
  NewTokenResponse,
  AuthToken,
} from '../types/serviceAccount'
import {
  LIST_SERVICE_ACCOUNTS,
  CREATE_SERVICE_ACCOUNT,
  DELETE_SERVICE_ACCOUNT,
  LIST_SA_TOKENS,
  CREATE_SA_TOKEN,
  DELETE_SA_TOKEN,
} from '../remote-routes'

const serviceAccountErrors: Record<number, string> = {
  400: 'Invalid request. Please check your input.',
  409: 'A service account with that name already exists.',
}

/**
 * Get list of all service accounts
 */
export async function getServiceAccounts(): Promise<ApiResult<ServiceAccountInfo[]>> {
  return apiGet<ServiceAccountInfo[]>(LIST_SERVICE_ACCOUNTS, undefined, { noCache: true })
}

/**
 * Create a new service account
 */
export async function createServiceAccount(
  data: NewServiceAccount
): Promise<ApiResult<ServiceAccountCreated>> {
  return apiPost<ServiceAccountCreated>(CREATE_SERVICE_ACCOUNT, data, undefined, {
    customErrors: serviceAccountErrors,
  })
}

/**
 * Delete a service account
 */
export async function deleteServiceAccount(name: string): Promise<ApiResult<void>> {
  return apiDelete<void>(DELETE_SERVICE_ACCOUNT(name), undefined, {
    customErrors: serviceAccountErrors,
  })
}

/**
 * Get tokens for a service account
 */
export async function getTokens(name: string): Promise<ApiResult<AuthToken[]>> {
  return apiGet<AuthToken[]>(LIST_SA_TOKENS(name), undefined, { noCache: true })
}

/**
 * Create a new token for a service account
 */
export async function createToken(
  name: string,
  tokenName?: string
): Promise<ApiResult<NewTokenResponse>> {
  const data: NewTokenRequest = tokenName ? { token_name: tokenName } : {}
  return apiPost<NewTokenResponse>(CREATE_SA_TOKEN(name), data)
}

/**
 * Delete a token from a service account
 */
export async function deleteToken(name: string, tokenId: number): Promise<ApiResult<void>> {
  return apiDelete<void>(DELETE_SA_TOKEN(name, tokenId))
}
